import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";
import MemberEditor from "./MemberEditor";

export default function Members({ openId, onConsumed }) {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const blank = { business_name: "", owner_name: "", borough: "Shoreditch", category: "", business_category: "Services", bio: "", personal_bio: "", business_bio: "", services: "", offers: "", email: "", address: "", website: "", shop_url: "", mobile: "", landline: "", display_phone: true, use_landline: false, instagram: "", tiktok: "", linkedin: "", ref_notes: "" };
  const [form, setForm] = useState(blank);
  const [pendingPhotos, setPendingPhotos] = useState([]);

  const load = async () => { const r = await client.get("/admin/members"); setMembers(r.data); return r.data; };
  useEffect(() => { load(); }, []);
  const [invites, setInvites] = useState([]);
  const [showPasses, setShowPasses] = useState(false);
  const loadInvites = () => client.get("/admin/invites").then((r) => setInvites(r.data)).catch(() => {});
  useEffect(() => { loadInvites(); }, []);
  const newPass = async () => {
    try { await client.post("/admin/invites", {}); await loadInvites(); setShowPasses(true); toast.success("Guest pass created — copy the link and send it."); }
    catch { toast.error("Could not create a pass."); }
  };
  const copyPass = (t) => {
    navigator.clipboard.writeText(`${window.location.origin}/join?pass=${t}`).then(() => toast.success("Invite link copied — send it to your guest."));
  };
  const togglePass = async (t) => { try { await client.post(`/admin/invites/${t}/toggle`); loadInvites(); } catch { toast.error("Could not update the pass."); } };
  const deletePass = async (t) => {
    if (!window.confirm("Delete this guest pass? The link will stop working immediately.")) return;
    try { await client.delete(`/admin/invites/${t}`); loadInvites(); toast.success("Pass deleted."); }
    catch { toast.error("Could not delete the pass."); }
  };
  useEffect(() => {
    const onPop = () => setSelected(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const openMember = (mm) => { window.history.pushState({ hub: mm.id }, "", "/admin"); setSelected(mm); };
  useEffect(() => {
    if (openId && members.length) {
      const mm = members.find((x) => x.id === openId);
      if (mm) { openMember(mm); onConsumed?.(); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, members]);
  const reselect = async (id) => { const data = await load(); const mm = data.find((x) => x.id === id) || null; if (mm) window.history.pushState({ hub: id }, "", "/admin"); setSelected(mm); };

  const create = async () => {
    if (!form.business_name.trim() || !form.owner_name.trim()) { toast.error("Just a business name and owner name to start — you'll fill everything else in the studio."); return; }
    setBusy(true);
    try {
      const r = await client.post("/admin/members", { ...form, category: form.category || "Other", bio: form.bio || "" });
      toast.success("Profile created — opening the full studio…");
      setForm(blank); setCreating(false);
      if (pendingPhotos.length) {
        try { await client.post(`/admin/members/${r.data.id}/import-photos`, { urls: pendingPhotos }); toast.success("Their website photos were pulled in too."); }
        catch { toast.error("Some photos couldn't be pulled — upload them in the studio."); }
        setPendingPhotos([]);
      }
      await reselect(r.data.id);
    } catch { toast.error("Could not create member."); }
    setBusy(false);
  };

  if (selected) return <MemberEditor member={selected} onBack={async () => { await load(); setSelected(null); }} onChange={() => load()} />;

  return (
    <div>
      <Header kicker="Production" title="Members">
        <div className="flex items-center gap-4">
          <span className="overline text-ink/50">{members.length} total</span>
          <a href="/portal?preview=1" target="_blank" rel="noreferrer" className="overline border border-ink/25 px-4 py-2 flex items-center gap-2 hover:border-ink transition-colors" data-testid="preview-member-form" title="Open the empty member onboarding form in a new tab, in editor preview mode">
            <FileText size={14} /> Preview form template
          </a>
          <button onClick={() => setShowPasses((s) => !s)} className="overline border border-ink px-4 py-2 flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors" data-testid="guest-passes-btn">
            <Gift size={14} /> Guest passes
          </button>
          <button onClick={() => setCreating((c) => !c)} className="overline bg-ink text-cream px-4 py-2 flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="add-member-btn">
            <Plus size={14} /> Add member
          </button>
        </div>
      </Header>
      <div className="p-6 sm:p-10">
        {showPasses && (
          <div className="border border-ink/20 p-6 mb-8" data-testid="guest-passes-panel">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
              <div>
                <p className="overline text-ink/60">Guest passes — free membership links</p>
                <p className="font-sans text-sm text-ink/60 mt-1 max-w-xl">Send a link to a friend: they register and go straight into the Members' Portal to build their profile — no payment. Switch any pass off to stop it working.</p>
              </div>
              <button onClick={newPass} className="overline bg-ink text-cream px-4 py-2 flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors shrink-0" data-testid="new-guest-pass">
                <Plus size={13} /> New pass
              </button>
            </div>
            {invites.length === 0 && <p className="font-mono text-xs text-ink/45" data-testid="no-passes">No passes yet — create one and copy the link.</p>}
            {invites.map((i) => (
              <div key={i.token} className="flex flex-wrap items-center gap-3 border-t border-ink/10 py-3" data-testid={`invite-${i.token}`}>
                <code className="font-mono text-xs bg-secondary px-2 py-1 break-all">{`${window.location.origin}/join?pass=${i.token}`}</code>
                <span className="font-mono text-[10px] text-ink/40">{i.uses || 0} used</span>
                <span className={`overline text-[9px] px-2 py-0.5 ${i.active ? "bg-ink text-cream" : "bg-secondary text-ink/50"}`}>{i.active ? "Live" : "Off"}</span>
                <button onClick={() => copyPass(i.token)} className="overline border border-ink/40 px-3 py-1.5 hover:border-ink transition-colors" data-testid={`copy-invite-${i.token}`}>Copy link</button>
                <button onClick={() => togglePass(i.token)} className="overline text-ink/50 hover:text-ink transition-colors" data-testid={`toggle-invite-${i.token}`}>{i.active ? "Switch off" : "Switch on"}</button>
                <button onClick={() => deletePass(i.token)} className="overline text-red-600/70 hover:text-red-600 transition-colors flex items-center gap-1.5" data-testid={`delete-invite-${i.token}`}><Trash2 size={12} /> Delete</button>
              </div>
            ))}
          </div>
        )}
        {creating && (
          <div className="bg-ink text-cream p-6 sm:p-8 grain mb-8" data-testid="add-member-panel">
            <div className="relative z-10 space-y-4">
              <p className="overline text-gloss-gold">New member profile — the whole form</p>
              <p className="font-sans text-sm text-cream/70 -mt-2">Fill in as much or as little as you like — only <b>business name</b> and <b>owner name</b> are required to create. Everything can still be edited or AI-generated later in the studio.</p>
              <p className="font-sans text-sm text-cream/70"><b className="text-gloss-gold">Optional:</b> paste their website below and hit Scan — the AI drafts the whole form for you. Or skip it entirely and type everything yourself.</p>
              <WebsiteImport dark defaultUrl={form.website} onApply={(p, photos, mode) => { setForm((f) => mergeProfile(f, p, mode)); setPendingPhotos(photos || []); }} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DIn v={form.business_name} on={(x) => setForm({ ...form, business_name: x })} ph="Business name *" testid="am-business" />
                <DIn v={form.owner_name} on={(x) => setForm({ ...form, owner_name: x })} ph="Owner name *" testid="am-owner" />
                <DIn v={form.borough} on={(x) => setForm({ ...form, borough: x })} list="am-borough-list" ph="Location / area (type or pick)" testid="am-borough" />
                <datalist id="am-borough-list">{LONDON_AREAS.map((b) => <option key={b} value={b} />)}</datalist>
                <DIn v={form.category} on={(x) => setForm({ ...form, category: x })} ph="Trade / type e.g. Bakery, Fintech" testid="am-category" />
                <select value={form.business_category} onChange={(e) => setForm({ ...form, business_category: e.target.value })} className="bg-ink border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="am-business-category">{BUSINESS_CATS.map((b) => <option key={b} value={b}>{b}</option>)}</select>
                <DIn v={form.address} on={(x) => setForm({ ...form, address: x })} ph="Full business address" testid="am-address" />
                <DIn v={form.website} on={(x) => setForm({ ...form, website: x })} ph="Website" testid="am-website" />
                <DIn v={form.email} on={(x) => setForm({ ...form, email: x })} ph="Email" testid="am-email" />
                <DIn v={form.mobile} on={(x) => setForm({ ...form, mobile: x })} ph="Mobile" testid="am-mobile" />
                <DIn v={form.landline} on={(x) => setForm({ ...form, landline: x })} ph="Landline (optional)" testid="am-landline" />
                <DIn v={form.instagram} on={(x) => setForm({ ...form, instagram: x })} ph="Instagram (optional)" testid="am-instagram" />
                <DIn v={form.tiktok} on={(x) => setForm({ ...form, tiktok: x })} ph="TikTok (optional)" testid="am-tiktok" />
                <DIn v={form.linkedin} on={(x) => setForm({ ...form, linkedin: x })} ph="LinkedIn (optional)" testid="am-linkedin" />
              </div>
              <label className="flex items-center gap-2.5 cursor-pointer" data-testid="am-display-phone">
                <input type="checkbox" checked={form.display_phone} onChange={(e) => setForm({ ...form, display_phone: e.target.checked })} className="w-4 h-4 accent-gloss-gold" />
                <span className="font-sans text-sm text-cream/70">Show phone number(s) in published campaigns</span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer -mt-2" data-testid="am-use-landline">
                <input type="checkbox" checked={form.use_landline} onChange={(e) => setForm({ ...form, use_landline: e.target.checked })} className="w-4 h-4 accent-gloss-gold" />
                <span className="font-sans text-sm text-cream/70">Also use landline in campaigns</span>
              </label>
              <DArea v={form.personal_bio} on={(x) => setForm({ ...form, personal_bio: x })} ph="Personal bio — about the owner (optional)" testid="am-personal-bio" />
              <DArea v={form.business_bio} on={(x) => setForm({ ...form, business_bio: x })} ph="Business bio — about the business (optional)" testid="am-business-bio" />
              <DArea v={form.bio} on={(x) => setForm({ ...form, bio: x })} ph="Story / short bio (optional — or write it with AI in the studio)" testid="am-bio" />
              <DArea v={form.services} on={(x) => setForm({ ...form, services: x })} ph="Services / specialities (optional)" testid="am-services" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DIn v={form.offers} on={(x) => setForm({ ...form, offers: x })} ph="Reader offer (optional)" testid="am-offers" />
              </div>
              <DArea v={form.ref_notes} on={(x) => setForm({ ...form, ref_notes: x })} ph="Links & notes for the AI (optional — press links, anything that describes them)" testid="am-ref-notes" />
              <div className="flex gap-3">
                <button onClick={create} disabled={busy} className="bg-cream text-ink px-6 py-3 overline flex items-center gap-2 hover:bg-gloss-gold transition-colors disabled:opacity-60" data-testid="am-save">
                  {busy ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />} Create &amp; open studio
                </button>
                <button onClick={() => { setCreating(false); setForm(blank); setPendingPhotos([]); }} className="border border-cream/40 px-6 py-3 overline hover:bg-cream/10 transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        )}
        <div className="border border-ink/15" data-testid="members-table">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-ink text-cream overline">
            <span className="col-span-4">Business</span>
            <span className="col-span-2 hidden sm:block">Borough</span>
            <span className="col-span-2 hidden sm:block">Status</span>
            <span className="col-span-3 hidden sm:block">Package</span>
            <span className="col-span-8 sm:col-span-1 text-right">Open</span>
          </div>
          {members.map((m) => (
            <div key={m.id} className="grid grid-cols-12 gap-4 px-5 py-4 border-t border-ink/10 items-center" data-testid="member-row">
              <div className="col-span-9 sm:col-span-4">
                <p className="font-serif text-lg">{m.business_name}</p>
                <p className="font-sans text-sm text-ink/60">{m.owner_name}</p>
              </div>
              <span className="col-span-2 hidden sm:block font-sans text-sm">{m.borough}</span>
              <span className="col-span-2 hidden sm:block">
                <Badge status={m.status} />
                {m.pending_review && <span className="ml-1.5 overline text-[9px] bg-gloss-gold text-ink px-1.5 py-0.5" data-testid={`pending-review-${m.id}`}>Updated</span>}
              </span>
              <span className="col-span-3 hidden sm:block font-mono text-xs text-ink/60">{m.package?.feature ? "Generated" : "—"}{m.cover_image ? " · Cover" : ""}</span>
              <div className="col-span-3 sm:col-span-1 flex justify-end">
                <button onClick={() => openMember(m)} className="overline border border-ink px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors" data-testid={`open-member-${m.id}`}>Open</button>
              </div>
            </div>
          ))}
          {members.length === 0 && <p className="px-5 py-10 text-center font-sans text-ink/50">No members yet.</p>}
        </div>
      </div>
    </div>
  );
}

